// src/interfaces/manager/ManagerInterface.js
import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import ManagerNavigation from './ManagerNavigation';
import AdminPanel from './AdminPanel';
import MaterialsManager from './MaterialsManager';
import DiscountTiersManager from './DiscountTiersManager';
import UserManagement from './UserManagement';
import CurveEditor from '../../components/small-format/admin/CurveEditor';
import DatabaseManager from '../../components/admin/DatabaseManager';
import ManagerCalculatorInterface from './ManagerCalculatorInterface';
import CustomPricingList from './CustomPricingList';
import PartnerDashboard from './PartnerDashboard';

const ManagerInterface = () => {
  const { currentUser, loading, isAdmin, hasRole } = useAuth();
  const [activeSection, setActiveSection] = useState('quotes');
  const [department, setDepartment] = useState('all');
  const [showSettingsMode, setShowSettingsMode] = useState(false);

  // Handle section changes to automatically switch modes
  const handleSectionChange = (section) => {
    setActiveSection(section);
    // Switch to operations mode if selecting an operations section
    if (['quotes', 'pricingCalculator', 'customPricing', 'partnerInsights'].includes(section)) {
      setShowSettingsMode(false);
    }
    // Switch to settings mode if selecting an admin section
    else if (['discounts', 'pricingCurves', 'database', 'userManagement'].includes(section)) {
      setShowSettingsMode(true);
    }
  };

  console.log(
    `%cManagerInterface: Rendering START - activeSection: ${activeSection}, department: ${department}`,
    "color: orange; font-weight: bold;"
  );

  // User can access manager portal if they are 'manager' OR 'admin'
  const canAccessManagerPortal = currentUser && (hasRole('manager') || hasRole('admin'));

  if (loading) {
    console.log("%cManagerInterface: Rendering Loading State", "color: gray;");
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  // Render the appropriate component based on active section
  const renderActiveSection = () => {
    console.log(
      `%cManagerInterface: renderActiveSection() called with activeSection = ${activeSection}`,
      "color: purple;"
    );
    switch (activeSection) {
      case 'quotes':
        console.log("%cManagerInterface: renderActiveSection returning AdminPanel", "color: purple;");
        return <AdminPanel departmentHint={department} />;
      
      case 'pricingCalculator': // Pricing Calculator case
        console.log("%cManagerInterface: renderActiveSection returning ManagerCalculatorInterface", "color: purple;");
        return <ManagerCalculatorInterface />;
      
      case 'materials':
        if (isAdmin()) {
          if (department === 'large-format') {
            console.log("%cManagerInterface: renderActiveSection returning MaterialsManager for admin", "color: purple;");
            return <MaterialsManager />;
          } else {
            console.log("%cManagerInterface: renderActiveSection returning Small Format Materials Placeholder for admin", "color: purple;");
            return (
              <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md text-center">
                <h2 className="text-xl font-bold mb-4">Small Format Materials (Admin)</h2>
                <p className="text-gray-600">
                  Small format materials management is currently in development.
                </p>
              </div>
            );
          }
        } else { // Not admin, show access denied for this section
          console.log("%cManagerInterface: renderActiveSection returning Access Denied for Materials section (non-admin)", "color: red;");
          return (
            <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md text-center">
              <h2 className="text-xl font-bold mb-4 text-red-600">Access Denied</h2>
              <p className="text-gray-600">You do not have permission to manage materials and pricing.</p>
            </div>
          );
        }
      
      case 'discounts': // Discount Management
        if (isAdmin()) {
          console.log(
            "%cManagerInterface: renderActiveSection returning DiscountTiersManager for admin",
            "color: purple; font-weight: bold;"
          );
          return <DiscountTiersManager />;
        } else { // Not admin, show access denied for this section
          console.log("%cManagerInterface: renderActiveSection returning Access Denied for Discounts section (non-admin)", "color: red;");
          return (
            <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md text-center">
              <h2 className="text-xl font-bold mb-4 text-red-600">Access Denied</h2>
              <p className="text-gray-600">You do not have permission to manage volume discounts.</p>
            </div>
          );
        }
      
      case 'pricingCurves': // Pricing curves section
        if (isAdmin()) {
          console.log(
            "%cManagerInterface: renderActiveSection returning CurveEditor for admin",
            "color: purple; font-weight: bold;"
          );
          return <CurveEditor />;
        } else {
          console.log("%cManagerInterface: renderActiveSection returning Access Denied for Pricing Curves section (non-admin)", "color: red;");
          return (
            <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md text-center">
              <h2 className="text-xl font-bold mb-4 text-red-600">Access Denied</h2>
              <p className="text-gray-600">You do not have permission to manage pricing curves.</p>
            </div>
          );
        }
      
      case 'database': // Database management section
        if (isAdmin()) {
          console.log(
            "%cManagerInterface: renderActiveSection returning DatabaseManager for admin",
            "color: purple; font-weight: bold;"
          );
          return <DatabaseManager />;
        } else {
          console.log("%cManagerInterface: renderActiveSection returning Access Denied for Database section (non-admin)", "color: red;");
          return (
            <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md text-center">
              <h2 className="text-xl font-bold mb-4 text-red-600">Access Denied</h2>
              <p className="text-gray-600">You do not have permission to manage the database.</p>
            </div>
          );
        }
      
      case 'reports':
        console.log("%cManagerInterface: renderActiveSection returning Reports Placeholder", "color: purple;");
        return (
          <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md text-center">
            <h2 className="text-xl font-bold mb-4">Reports</h2>
            <p className="text-gray-600">
              Reporting features are currently in development.
            </p>
          </div>
        );
      
      case 'userManagement': // User management
        console.log("%cManagerInterface: renderActiveSection returning UserManagement", "color: purple;");
        return isAdmin() ? <UserManagement /> : <div className="text-red-500 text-center p-4">Access Denied. Admin role required.</div>;

      case 'customPricing':
        console.log("%cManagerInterface: renderActiveSection returning CustomPricingList", "color: purple;");
        return <CustomPricingList />;

      case 'partnerInsights':
        console.log("%cManagerInterface: renderActiveSection returning PartnerDashboard", "color: purple;");
        return <PartnerDashboard />;
      
      default:
        console.log("%cManagerInterface: renderActiveSection returning null (default case)", "color: purple;");
        return null;
    }
  };

  if (!canAccessManagerPortal && !loading) { // Use canAccessManagerPortal
    // --- LOG 5: Log if rendering Access Denied ---
    console.log("%cManagerInterface: Rendering Access Denied", "color: red;");
    return (
      <div className="max-w-md mx-auto bg-white p-8 mt-8 rounded-lg shadow-md text-center">
        <h2 className="text-xl font-semibold mb-4">Access Denied</h2>
        <p className="text-gray-600 mb-4">You don't have permission to access the Manager Portal.</p>
        <p className="text-gray-600">Please log in with a manager account to view this section.</p>
      </div>
    );
  }

  // --- LOG 6: Log right before returning the main JSX ---
  console.log("%cManagerInterface: Preparing to return main JSX", "color: orange;");
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-sky-50 to-slate-200">
      {/* Pass props to ManagerNavigation if needed */}
      <ManagerNavigation
        activeSection={activeSection}
        setActiveSection={handleSectionChange}
        showSettingsMode={showSettingsMode}
        setShowSettingsMode={setShowSettingsMode}
      />

      {/* Special handling for pricing calculator - render directly without container/header */}
      {activeSection === 'pricingCalculator' ? (
        renderActiveSection()
      ) : (
        <div className={`px-4 py-6 ${['quotes', 'partnerInsights'].includes(activeSection) ? 'w-full' : 'container mx-auto'}`}>
          <header className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">
              {activeSection === 'quotes' ? 'Quote Management' :
               activeSection === 'materials' ? 'Materials & Pricing' :
               activeSection === 'discounts' ? 'Volume Discounts' :
               activeSection === 'pricingCurves' ? 'Pricing Curves' :
                activeSection === 'database' ? 'Database Management' :
                activeSection === 'reports' ? 'Reports' :
                activeSection === 'userManagement' ? 'User Management' :
                activeSection === 'customPricing' ? 'Custom Pricing' :
                activeSection === 'partnerInsights' ? 'Partner Insights' :
                'Manager Portal'}
            </h1>
            <p className="text-gray-600 mt-2">
              {activeSection === 'quotes' ? 'Review and manage customer quotes' :
               activeSection === 'materials' ? 'Manage materials, pricing, and product configurations' :
               activeSection === 'discounts' ? 'Configure volume-based discount tiers' :
               activeSection === 'pricingCurves' ? 'Manage pricing curves and cost calculations' :
                activeSection === 'database' ? 'Direct database table management' :
                activeSection === 'reports' ? 'View business reports and analytics' :
                activeSection === 'userManagement' ? 'Manage user accounts and permissions' :
                 activeSection === 'customPricing' ? 'View customers with assigned custom pricing and products' :
                 activeSection === 'partnerInsights' ? 'Understand external customer ordering behaviour and opportunities' :
                'Administrative tools and management functions'}
            </p>
          </header>

          {/* Department Selector - Hide for sections that don't need it */}
          {activeSection !== 'pricingCurves' && 
           activeSection !== 'database' && 
            activeSection !== 'reports' && 
            activeSection !== 'userManagement' && 
             activeSection !== 'customPricing' &&
             activeSection !== 'partnerInsights' &&
            activeSection !== 'pricingCalculator' && (
            <div className="max-w-md mx-auto mb-8">
              <div className="bg-white rounded-lg shadow-md p-4">
                <h2 className="text-lg font-semibold mb-3 text-center">Select Department Focus</h2>
                <div className="flex">
                  <button
                    className={`flex-1 py-2 px-4 text-sm sm:text-base rounded-l-lg ${
                      department === 'large-format'
                        ? 'bg-[#fafa00] text-black'
                        : 'bg-gray-200 text-gray-800'
                    }`}
                    onClick={() => setDepartment('large-format')}
                  >
                    Large Format
                  </button>
                  <button
                    className={`flex-1 py-2 px-4 text-sm sm:text-base ${
                      department === 'all'
                        ? 'bg-green-500 text-black' 
                        : 'bg-gray-200 text-gray-800'
                    }`}
                    onClick={() => setDepartment('all')}
                  >
                    All Departments
                  </button>
                  <button
                    className={`flex-1 py-2 px-4 text-sm sm:text-base rounded-r-lg ${
                      department === 'small-format'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-800'
                    }`}
                    onClick={() => setDepartment('small-format')}
                  >
                    Small Format
                  </button>
                </div>
              </div>
            </div>
          )}

          <main>
            {/* --- LOG 7: Log before rendering the main content section --- */}
            {console.log(
              "%cManagerInterface: Rendering <main> - BEFORE active section component",
              "color: cyan;"
            )}

            {/* Render the active section component */}
            {renderActiveSection()}

            {/* --- LOG 8: Log after attempting to render the active section component --- */}
            {console.log(
              "%cManagerInterface: Rendering <main> - AFTER active section component",
              "color: cyan;"
            )}

            {/* Show coming soon placeholders */}
            {activeSection === 'materials' && department === 'small-format' && (
               <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md text-center">
                <h2 className="text-xl font-bold mb-4">Small Format Materials (Admin)</h2>
                <p className="text-gray-600">
                  Small format materials management is currently in development.
                </p>
              </div>
            )}
            {activeSection === 'reports' && (
               <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md text-center">
                <h2 className="text-xl font-bold mb-4">Reports</h2>
                <p className="text-gray-600">
                  Reporting features are currently in development.
                </p>
              </div>
            )}
          </main>
        </div>
      )}
    </div>
  );
};

export default ManagerInterface;
